import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createInvestment } from '../../api/Index';
import './CreateInvestment.css';
import PageTitle from '../../components/UI/PageTitle';

const CreateInvestment = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    amountInvested: '',
    expectedReturn: '',
    startDate: '',
    expectedEndDate: '',
    category: '',
    description: '',
    notes: '',
    status: 'pending',
    riskLevel: 'medium'
  });
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleStatusChange = (status) => {
    setFormData({
      ...formData,
      status
    });
    setError(null);
  };

  const handleRiskLevelChange = (riskLevel) => {
    setFormData({
      ...formData,
      riskLevel
    });
    setError(null);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    
    if (!formData.amountInvested || isNaN(parseFloat(formData.amountInvested)) || parseFloat(formData.amountInvested) <= 0) {
      setError('Please enter a valid investment amount');
      return false;
    }
    
    if (!formData.expectedReturn || isNaN(parseFloat(formData.expectedReturn)) || parseFloat(formData.expectedReturn) <= 0) {
      setError('Please enter a valid expected return');
      return false;
    }
    
    if (!formData.startDate) {
      setError('Start date is required');
      return false;
    }
    
    if (!formData.expectedEndDate) {
      setError('Expected end date is required');
      return false;
    }
    
    if (new Date(formData.expectedEndDate) <= new Date(formData.startDate)) {
      setError('Expected end date must be after start date');
      return false;
    }
    
    if (!formData.description.trim()) {
      setError('Description is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) return;
    
    setCreating(true);
    try {
      // Prepare data for API - map frontend fields to backend fields
      const investmentData = {
        name: formData.title,
        amount: parseFloat(formData.amountInvested),
        expectedReturn: parseFloat(formData.expectedReturn),
        startDate: formData.startDate,
        endDate: formData.expectedEndDate,
        description: formData.description
      };
      
      const response = await createInvestment(investmentData);
      
      // Redirect to investments page
      navigate('/investments');
      
    } catch (err) {
      console.error('Error creating investment:', err);
      setError(err.response?.data?.msg || err.message || 'Failed to create investment');
      setCreating(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffc107';
      case 'active':
        return '#28a745';
      case 'completed':
        return '#007bff';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low':
        return '#28a745';
      case 'medium':
        return '#ffc107';
      case 'high':
        return '#fd7e14';
      case 'very high':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };
  
  return (
    <div>
        <PageTitle title='Create Investment' />
        <div className="create-investment-container">
            <div className="form-container">
                {error && (
                    <div style={{
                        backgroundColor: '#ffe6e6',
                        border: '1px solid #ff0000',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px',
                        color: '#cc0000'
                    }}>
                        {error}
                    </div>
                )}
                
                <form className="create-investment-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label className="input-label">Title *</label>
                        <input
                            type="text"
                            name="title"
                            className="input"
                            placeholder="Enter Investment Title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Amount Invested *</label>
                        <input
                            type="number"
                            name="amountInvested"
                            className="input"
                            placeholder="Enter Invested Amount"
                            value={formData.amountInvested}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className='status-group'>
                        <label className="input-label">Investment Status</label>
                        <div className='status-label-group'>
                            <button
                                type="button"
                                onClick={() => handleStatusChange('pending')}
                                style={{
                                    backgroundColor: formData.status === 'pending' ? getStatusColor('pending') : '#f8f9fa',
                                    color: formData.status === 'pending' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Pending
                            </button>
                            <button
                                type="button"
                                onClick={() => handleStatusChange('active')}
                                style={{
                                    backgroundColor: formData.status === 'active' ? getStatusColor('active') : '#f8f9fa',
                                    color: formData.status === 'active' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                onClick={() => handleStatusChange('completed')}
                                style={{
                                    backgroundColor: formData.status === 'completed' ? getStatusColor('completed') : '#f8f9fa',
                                    color: formData.status === 'completed' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Completed
                            </button>
                            <button
                                type="button"
                                onClick={() => handleStatusChange('cancelled')}
                                style={{
                                    backgroundColor: formData.status === 'cancelled' ? getStatusColor('cancelled') : '#f8f9fa',
                                    color: formData.status === 'cancelled' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelled
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Expected Return *</label>
                        <input
                            type="number"
                            name="expectedReturn"
                            className="input"
                            placeholder="Enter Expected Return"
                            value={formData.expectedReturn}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Start Date *</label>
                        <input
                            type="date"
                            name="startDate"
                            className="date-input"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className='input-group'>
                        <label className="input-label">Expected End Date *</label>
                        <input
                            type="date"
                            name="expectedEndDate"
                            className="date-input"
                            value={formData.expectedEndDate}
                            onChange={handleInputChange}
                            min={formData.startDate || undefined}
                            required
                        />
                    </div>

                    <div className='status-group'>
                        <label className="input-label">Risk Level</label>
                        <div className='status-label-group'>
                            <button
                                type="button"
                                onClick={() => handleRiskLevelChange('low')}
                                style={{
                                    backgroundColor: formData.riskLevel === 'low' ? getRiskLevelColor('low') : '#f8f9fa',
                                    color: formData.riskLevel === 'low' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Low
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRiskLevelChange('medium')}
                                style={{
                                    backgroundColor: formData.riskLevel === 'medium' ? getRiskLevelColor('medium') : '#f8f9fa',
                                    color: formData.riskLevel === 'medium' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                Medium
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRiskLevelChange('high')}
                                style={{
                                    backgroundColor: formData.riskLevel === 'high' ? getRiskLevelColor('high') : '#f8f9fa',
                                    color: formData.riskLevel === 'high' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                High
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRiskLevelChange('very high')}
                                style={{
                                    backgroundColor: formData.riskLevel === 'very high' ? getRiskLevelColor('very high') : '#f8f9fa',
                                    color: formData.riskLevel === 'very high' ? 'white' : '#333',
                                    border: '1px solid #ddd',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Very High
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Category</label>
                        <input
                            type="text"
                            name="category"
                            className="input"
                            placeholder="Enter Category (for reference only)"
                            value={formData.category}
                            onChange={handleInputChange}
                        />
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                            Category, Status, Risk Level, and Notes are for UI reference only
                        </small>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Description *</label>
                        <textarea
                            name="description"
                            className="text-area"
                            placeholder="Enter Investment Description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows={4}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Notes</label>
                        <textarea
                            name="notes"
                            className="text-area"
                            placeholder="Enter relevant investment notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            rows={3}
                        />
                    </div>

                    <button 
                        type="submit" 
                        className='form-button'
                        disabled={creating}
                        style={{
                            opacity: creating ? 0.6 : 1,
                            cursor: creating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {creating ? 'Creating Investment...' : 'Create Investment'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default CreateInvestment;